import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSupportNotifications } from '../context/SupportNotificationsContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../context/LanguageContext';
import { useMobileDetection } from '../hooks/useIsMobile';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { getVersionDisplay, getEnvironment } from '../lib/version';
import { hasPermission, isAdminUser, getUserRoles, ROLE_LABELS } from '../lib/permissions';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import GlobalSearch from './search/GlobalSearch';
import FeedbackDialog from './FeedbackDialog';
import AnnouncementBanner from './AnnouncementBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Factory,
  Calendar, 
  ClipboardList,
  BarChart3, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  UserCog,
  Database,
  Upload,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  Sun,
  Moon,
  User,
  FileText,
  Rocket,
  Globe,
  Clock,
  MessageSquare,
  Bell,
  HelpCircle,
  ChevronDown,
  Smartphone,
  ArrowRight,
  AlertTriangle,
  Ticket,
  Mountain,
  FileCheck,
  MessageSquarePlus,
  Star,
  Receipt,
  FolderOpen,
  Loader2,
  Layers,
  CreditCard,
  Ban
} from 'lucide-react';
import { getAssetIcon } from '../utils/industryIcons';

const GracePeriodBanner = ({ expiresAt }) => {
  const [daysLeft, setDaysLeft] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
    setDaysLeft(diff);
  }, [expiresAt]);

  if (daysLeft === null) return null;

  const isUrgent = daysLeft <= 2;

  return (
    <div
      data-testid="grace-period-banner"
      className={`fixed top-0 left-0 right-0 z-[70] px-4 h-9 flex items-center justify-between text-sm font-medium ${
        isUrgent ? 'bg-red-500 text-white' : 'bg-[#ffd108] text-[#1a1a1a]'
      }`}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" />
        <span>
          {daysLeft === 0
            ? 'Your grace period expires today. Select a plan to avoid suspension.'
            : `Subscription required - ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining to select a plan.`}
        </span>
      </div>
      <Button
        size="sm"
        className={`h-7 px-3 text-xs font-semibold ${
          isUrgent ? 'bg-white text-red-600 hover:bg-gray-100' : 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]'
        }`}
        onClick={() => navigate('/settings?tab=billing')}
      >
        Choose Plan
      </Button>
    </div>
  );
};

const TrialBanner = ({ trialEndsAt }) => {
  const [daysLeft, setDaysLeft] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const expires = new Date(trialEndsAt);
    const now = new Date();
    const diff = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
    setDaysLeft(diff);
  }, [trialEndsAt]);

  if (daysLeft === null) return null;

  const isUrgent = daysLeft <= 3;

  return (
    <div
      data-testid="trial-banner"
      className={`fixed top-0 left-0 right-0 z-[70] px-4 h-9 flex items-center justify-between text-sm font-medium ${
        isUrgent ? 'bg-orange-500 text-white' : 'bg-[#ffd108] text-[#1a1a1a]'
      }`}
    >
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4" />
        <span data-testid="trial-banner-text">
          {daysLeft === 0
            ? 'Your free trial ends today. Add payment to keep your account active.'
            : `Free trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left. Add payment to continue after your trial.`}
        </span>
      </div>
      <Button
        size="sm"
        className={`h-7 px-3 text-xs font-semibold ${
          isUrgent ? 'bg-white text-orange-600 hover:bg-gray-100' : 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]'
        }`}
        onClick={() => navigate('/settings?tab=billing')}
        data-testid="trial-banner-cta"
      >
        Add Payment
      </Button>
    </div>
  );
};

const SuspendedBanner = () => {
  const navigate = useNavigate();
  return (
    <div
      data-testid="suspended-banner"
      className="fixed top-0 left-0 right-0 z-[70] px-4 h-10 flex items-center justify-between text-sm font-medium bg-red-600 text-white"
    >
      <div className="flex items-center gap-2">
        <Ban className="h-4 w-4" />
        <span data-testid="suspended-banner-text">
          Your account has been suspended due to an unpaid invoice. Update your billing to restore access.
        </span>
      </div>
      <Button
        size="sm"
        className="h-7 px-3 text-xs font-semibold bg-white text-red-600 hover:bg-gray-100"
        onClick={() => navigate('/settings?tab=billing')}
        data-testid="suspended-banner-cta"
      >
        <CreditCard className="h-3 w-3 mr-1" />
        Manage Plan
      </Button>
    </div>
  );
};

const Layout = () => {
  const { api, user, logout, isImpersonating, subscriptionStatus, gracePeriodExpiresAt, trialEndsAt, paymentProcessing } = useAuth();
  const { fmtDate } = useTenantSettings();
  const { theme, toggleTheme } = useTheme();
  const {
    unreadCount: unreadSupportCount,
    unreadTickets: unreadSupportTickets,
    markRead: markSupportTicketRead,
    generalNotifications,
    markGeneralRead,
  } = useSupportNotifications();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobileDetection();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('equipr_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false; // Default expanded
  });
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() => {
    return sessionStorage.getItem('mobile_banner_dismissed') === 'true';
  });
  const [isEarlyAdopter, setIsEarlyAdopter] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();

  // Save sidebar preference
  useEffect(() => {
    localStorage.setItem('equipr_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Suspended accounts: only allow /support/* and /settings?tab=billing
  useEffect(() => {
    if ((subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled') &&
        !location.pathname.startsWith('/support')) {
      // Allow billing settings through
      const params = new URLSearchParams(location.search);
      if (location.pathname === '/settings' && params.get('tab') === 'billing') return;
      navigate('/support', { replace: true });
    }
  }, [subscriptionStatus, location.pathname, location.search, navigate]);

  // Check early adopter status
  useEffect(() => {
    if (!api || !user) return;
    const checkEarlyAdopter = async () => {
      try {
        const res = await api.get('/tenants/settings');
        setIsEarlyAdopter(!!res.data?.early_adopter);
      } catch {
        // Non-critical check
      }
    };
    checkEarlyAdopter();
  }, [api, user]);

  const handleDismissMobileBanner = () => {
    setMobileBannerDismissed(true);
    sessionStorage.setItem('mobile_banner_dismissed', 'true');
  };

  // Relative time like "5m ago" / "3h ago" / "2d ago" for notification rows
  const timeAgo = (iso) => {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return fmtDate(iso);
  };

  const priorityDotClass = (p) => {
    switch ((p || '').toLowerCase()) {
      case 'urgent': return 'bg-red-500';
      case 'high':   return 'bg-orange-500';
      case 'medium': return 'bg-[#ffd108]';
      default:       return 'bg-slate-400';
    }
  };

  const handleNotificationClick = (ticketId) => {
    markSupportTicketRead(ticketId);
    navigate(`/support/tickets/${ticketId}`);
  };

  const handleClearNotification = (e, ticketId) => {
    e.stopPropagation();
    e.preventDefault();
    markSupportTicketRead(ticketId);
  };

  const handleClearAllNotifications = () => {
    const ids = (unreadSupportTickets || []).map((t) => t.id);
    ids.forEach((id) => markSupportTicketRead(id));
    (generalNotifications || []).forEach((n) => markGeneralRead(n.id));
  };

  const handleGeneralNotificationClick = (n) => {
    markGeneralRead(n.id);
    if (n.job_id) navigate(`/schedule?job=${n.job_id}`);
  };

  const handleSwitchToMobile = () => {
    // Use full page navigation since tenant-mobile is a separate router context
    window.location.href = '/tenant-mobile';
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors
    }
    logout();
    navigate('/login');
  };

  const isOperator = user?.role === 'operator';
  const isAdmin = isAdminUser(user);
  const canManageData = hasPermission(user, 'data_management.write');
  const canImportData = hasPermission(user, 'data_import.write');
  const isLightMode = theme === 'light';

  // State for Getting Started progress
  const [gettingStartedProgress, setGettingStartedProgress] = useState(null);
  
  // Fetch Getting Started progress (only for authenticated admins)
  useEffect(() => {
    const fetchProgress = async () => {
      // Ensure user is fully authenticated with client_id
      if (!api || !user || !user.client_id || isOperator) return;
      
      try {
        const [checklistRes, onboardingRes] = await Promise.all([
          api.get('/onboarding/checklist-status'),
          api.get('/users/me/onboarding')
        ]);
        const wizardComplete = onboardingRes.data?.wizard_completed || false;
        const items = checklistRes.data?.items || [];
        const completedItems = items.filter(i => i.completed && !i.optional).length;
        const totalRequired = items.filter(i => !i.optional).length;
        const total = totalRequired + 1; // +1 for onboarding wizard
        const completed = completedItems + (wizardComplete ? 1 : 0);
        setGettingStartedProgress({ 
          completed, 
          total, 
          allDone: checklistRes.data?.all_required_complete && wizardComplete, 
          dismissed: checklistRes.data?.checklist_dismissed 
        });
      } catch (e) {
        // Silently fail - user may have dismissed or is on basic tier
        // Getting started fetch skipped - non-critical
      }
    };
    
    // Small delay to ensure auth is fully established
    const timer = setTimeout(fetchProgress, 500);
    return () => clearTimeout(timer);
  }, [api, user?.client_id, isOperator]);

  // Main navigation items for sidebar
  // Get industry-specific asset icon
  const AssetIcon = getAssetIcon(user?.industry);
  
  const navItems = isOperator ? [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/my-jobs', icon: ClipboardList, label: t('nav.myJobs') },
  ] : [
    // Add Getting Started at the top if not complete and not dismissed
    ...(gettingStartedProgress && !gettingStartedProgress.allDone && !gettingStartedProgress.dismissed ? [
      { to: '/getting-started', icon: Rocket, label: 'Getting Started', badge: `${gettingStartedProgress.completed}/${gettingStartedProgress.total}`, highlight: true },
    ] : []),
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    ...(hasPermission(user, 'schedule.read') ? [
      { to: '/schedule', icon: Calendar, label: t('nav.schedule') },
    ] : []),
    ...(hasPermission(user, 'accounting.read') ? [
      { to: '/dry-hire-invoicing', icon: Receipt, label: 'Dry-Hire Invoicing' },
    ] : []),
    ...(hasPermission(user, 'customers.read') ? [
      { to: '/customers', icon: Building2, label: 'Customers & Sites' },
    ] : []),
    ...(hasPermission(user, 'suppliers.read') ? [
      { to: '/suppliers', icon: Factory, label: 'Subcontractors' },
    ] : []),
    ...(hasPermission(user, 'equipment.read') ? [
      { to: '/equipment', icon: AssetIcon, label: t('nav.equipment') },
    ] : []),
    ...(hasPermission(user, 'operators.read') ? [
      { to: '/workers', icon: Users, label: t('nav.operators') },
    ] : []),
    // Only show Tips & Materials for non-crane-hire industries
    ...(hasPermission(user, 'tips_materials.read') && user?.industry?.toLowerCase() !== 'crane_hire' && user?.industry?.toLowerCase() !== 'crane hire' ? [
      { to: '/tips-materials', icon: Mountain, label: 'Tips & Materials' },
    ] : []),
    ...(hasPermission(user, 'quotes.read') ? [
      { to: '/quotes', icon: FileCheck, label: 'Quotes' },
    ] : []),
    ...(hasPermission(user, 'dockets.read') ? [
      { to: '/dockets', icon: FileText, label: t('nav.dockets') },
    ] : []),
    ...(hasPermission(user, 'compliance.read') ? [
      { to: '/compliance', icon: Shield, label: t('nav.compliance') },
    ] : []),
    ...(hasPermission(user, 'timesheets.read') ? [
      { to: '/timesheets', icon: Clock, label: 'Timesheets' },
    ] : []),
    ...(hasPermission(user, 'chat.read') ? [
      { to: '/chat', icon: MessageSquare, label: 'Messages' },
    ] : []),
    ...(hasPermission(user, 'forms.read') ? [
      { to: '/forms', icon: ClipboardList, label: t('nav.forms') },
    ] : []),
    ...(hasPermission(user, 'documents.read') ? [
      { to: '/documents', icon: FolderOpen, label: 'Documents' },
    ] : []),
    ...(isFeatureEnabled('projects') && hasPermission(user, 'projects.read') ? [
      { to: '/projects', icon: Layers, label: t('nav.projects') },
    ] : []),
    ...(hasPermission(user, 'reports.read') ? [
      { to: '/reports', icon: BarChart3, label: t('nav.reports') },
    ] : []),
    ...(canManageData ? [
      { to: '/data-management', icon: Database, label: t('nav.dataManagement') },
    ] : []),
    ...(canImportData ? [
      { to: '/data-import', icon: Upload, label: 'Data Import' },
    ] : []),
  ];

  // Suspended/cancelled accounts: restrict sidebar to Support + Billing only
  const isSuspendedAccount = subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled';
  const effectiveNavItems = isSuspendedAccount
    ? [
        { to: '/support', icon: Ticket, label: 'Support' },
        { to: '/settings?tab=billing', icon: CreditCard, label: 'Billing' },
      ]
    : navItems;

  // Sidebar styling
  // Navigation theming — light mode uses a warm neutral charcoal mid-tone (Jira-style)
  const sidebarBg = isLightMode ? 'bg-zinc-800' : 'bg-zinc-900';
  const sidebarText = isLightMode ? 'text-zinc-400' : 'text-zinc-400';
  const sidebarActiveText = isLightMode ? 'text-white' : 'text-white';
  const sidebarHover = isLightMode ? 'hover:bg-zinc-700' : 'hover:bg-zinc-800';
  const sidebarActiveBg = isLightMode ? 'bg-zinc-700' : 'bg-zinc-800';
  const sidebarBorder = isLightMode ? 'border-zinc-700' : 'border-zinc-800';

  // Top bar styling — same warm neutral charcoal in light mode
  const topBarBg = isLightMode ? 'bg-zinc-800 border-b border-zinc-700' : 'bg-zinc-900 border-b border-zinc-800';
  const topBarText = isLightMode ? 'text-zinc-400' : 'text-zinc-400';

  const NavItem = ({ to, icon: Icon, label, badge, highlight }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    
    // Highlight colors — nav is always dark-ish, so use consistent gold highlights
    const highlightBg = 'bg-[#ffd108]/20';
    const highlightText = 'text-[#ffd108]';
    const highlightHover = 'hover:bg-[#ffd108]/30';
    const highlightBorder = 'border border-[#ffd108]/30';
    
    if (sidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <NavLink
              to={to}
              onClick={() => setMobileMenuOpen(false)}
              className={`relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                highlight 
                  ? `${highlightBg} ${highlightText} ${highlightHover}`
                  : isActive
                    ? `${sidebarActiveBg} ${sidebarActiveText}`
                    : `${sidebarText} ${sidebarHover}`
              }`}
            >
              <Icon className="h-5 w-5" />
              {badge && (
                <span className="absolute -top-1 -right-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#ffd108] text-black">
                  {badge}
                </span>
              )}
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium">
            {label} {badge && `(${badge})`}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <NavLink
        to={to}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          highlight
            ? `${highlightBg} ${highlightText} ${highlightHover} ${highlightBorder}`
            : isActive
              ? `${sidebarActiveBg} ${sidebarActiveText}`
              : `${sidebarText} ${sidebarHover}`
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="truncate flex-1">{label}</span>
        {badge && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#ffd108] text-black">
            {badge}
          </span>
        )}
      </NavLink>
    );
  };

  // Compute vertical offset so the fixed header + any top banner don't overlap content.
  // Trial/Grace/Suspended banner ≈ 40px (header sits at top-10), Mobile banner = 48px (header sits at top-12), else 0.
  const topBannerHeight =
    (subscriptionStatus === 'grace_period' && gracePeriodExpiresAt) ||
    (subscriptionStatus === 'trialing' && trialEndsAt) ||
    subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled' ||
    paymentProcessing
      ? 40
      : isMobile && !mobileBannerDismissed
        ? 48
        : 0;
  const contentTopOffset = topBannerHeight + 48; // 48 = header height (h-12)

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Mobile Banner - Shows when on mobile device */}
        {isMobile && !mobileBannerDismissed && (
          <div className="fixed top-0 left-0 right-0 bg-[#ffd108] text-[#1a1a1a] px-4 py-3 z-[60] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              <span className="text-sm font-medium">Switch to mobile view for a better experience</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] h-8 px-3"
                onClick={handleSwitchToMobile}
                data-testid="switch-to-mobile-btn"
              >
                Open Mobile
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <button
                onClick={handleDismissMobileBanner}
                className="p-1 hover:bg-[#1a1a1a]/10 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Grace Period Banner */}
        {subscriptionStatus === 'grace_period' && gracePeriodExpiresAt && (
          <GracePeriodBanner expiresAt={gracePeriodExpiresAt} />
        )}

        {/* Trial Countdown Banner */}
        {subscriptionStatus === 'trialing' && trialEndsAt && (
          <TrialBanner trialEndsAt={trialEndsAt} />
        )}

        {/* Suspended Banner */}
        {(subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled') && (
          <SuspendedBanner />
        )}

        {/* BECS Payment Processing Banner */}
        {paymentProcessing && !subscriptionStatus && (
          <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white px-4 py-2 z-[55] flex items-center justify-center gap-2 text-sm" data-testid="payment-processing-banner">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Your bank payment is being processed. This typically takes 2-4 business days.</span>
          </div>
        )}
        
        {/* Top Navigation Bar */}
        <header className={`fixed ${
          (subscriptionStatus === 'grace_period' && gracePeriodExpiresAt) ||
          (subscriptionStatus === 'trialing' && trialEndsAt) ||
          (subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled') ||
          (paymentProcessing && !subscriptionStatus)
            ? 'top-10' 
            : isMobile && !mobileBannerDismissed 
              ? 'top-12' 
              : 'top-0'
        } left-0 right-0 h-12 ${topBarBg} flex items-center justify-between px-4 z-50 transition-all`}>
          {/* Left side - Logo & Mobile menu */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${topBarText} ${isLightMode ? 'hover:bg-[#3a3a40]' : 'hover:bg-zinc-800'}`}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded overflow-hidden flex-shrink-0">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" data-testid="sidebar-company-logo" />
              </div>
              <span className="font-heading font-bold text-sm hidden sm:block text-white">
                {user?.company_name || 'EQUIPR CORE'}
              </span>
              {/* Demo Badge */}
              {user?.is_demo && (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-amber-500/20 text-amber-600 border-amber-500/50 font-semibold text-xs"
                  data-testid="demo-badge"
                >
                  DEMO
                </Badge>
              )}
            </div>
          </div>

          {/* Center - Search (desktop) */}
          {!isSuspendedAccount && (
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <GlobalSearch />
          </div>
          )}

          {/* Right side - Actions */}
          <div className="flex items-center gap-1">
            {/* Product Roadmap */}
            {!isSuspendedAccount && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/product-roadmap')}
                  className={`hidden sm:flex gap-1.5 ${topBarText}`}
                >
                  <Rocket className="h-4 w-4" />
                  <span className="hidden lg:inline text-xs">Roadmap</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Product Roadmap</TooltipContent>
            </Tooltip>
            )}

            {/* Early Adopter Feedback */}
            {isEarlyAdopter && !isSuspendedAccount && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFeedbackOpen(true)}
                    data-testid="early-adopter-feedback-btn"
                    className={`hidden sm:flex gap-1.5 border border-[#ffd108]/30 hover:border-[#ffd108]/60 hover:bg-[#ffd108]/10 ${topBarText}`}
                  >
                    <MessageSquarePlus className="h-4 w-4 text-[#ffd108]" />
                    <span className="hidden lg:inline text-xs text-[#ffd108]">Feedback</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send us feedback as an Early Adopter</TooltipContent>
              </Tooltip>
            )}

            {/* Help */}
            {!isSuspendedAccount && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-8 w-8 ${topBarText}`}
                  onClick={() => navigate('/help')}
                  data-testid="help-nav-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Help Centre</TooltipContent>
            </Tooltip>
            )}

            {/* Release Notes */}
            {!isSuspendedAccount && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 ${topBarText}`}
                  onClick={() => navigate('/release-notes')}
                  data-testid="release-notes-nav-btn"
                >
                  <Rocket className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Release Notes</TooltipContent>
            </Tooltip>
            )}

            {/* Support (nav only — unread count lives on the bell) */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-8 w-8 ${topBarText}`}
                  onClick={() => navigate('/support')}
                  data-testid="topbar-support-btn"
                >
                  <Ticket className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Support</TooltipContent>
            </Tooltip>

            {/* Notifications */}
            {!isSuspendedAccount && (
            <DropdownMenu>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 relative ${unreadSupportCount > 0 ? 'text-[#ffd108]' : topBarText}`}
                      data-testid="topbar-notifications-btn"
                    >
                      <Bell className="h-4 w-4" />
                      {unreadSupportCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ffd108] text-[#1a1a1a] text-[10px] font-bold flex items-center justify-center"
                          data-testid="topbar-notifications-badge"
                        >
                          {unreadSupportCount > 9 ? '9+' : unreadSupportCount}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  {unreadSupportCount > 0
                    ? `Notifications · ${unreadSupportCount} unread`
                    : 'Notifications'}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent
                align="end"
                className="w-80 p-0"
                data-testid="notifications-dropdown"
              >
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-sm font-semibold">Notifications</span>
                  <div className="flex items-center gap-2">
                    {unreadSupportCount > 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        {unreadSupportCount} unread
                      </span>
                    )}
                    {((unreadSupportTickets && unreadSupportTickets.length > 0) ||
                      (generalNotifications && generalNotifications.length > 0)) && (
                      <button
                        type="button"
                        onClick={handleClearAllNotifications}
                        className="text-[11px] text-[#ffd108] hover:underline"
                        data-testid="notifications-clear-all"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                {((!unreadSupportTickets || unreadSupportTickets.length === 0) &&
                  (!generalNotifications || generalNotifications.length === 0)) ? (
                  <div
                    className="px-3 py-6 text-center text-xs text-muted-foreground"
                    data-testid="notifications-empty"
                  >
                    You're all caught up.
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto">
                    {(generalNotifications || []).map((n) => (
                      <div
                        key={n.id}
                        className="group relative w-full flex gap-2 items-start border-b last:border-b-0 hover:bg-muted/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => handleGeneralNotificationClick(n)}
                          className="flex-1 text-left px-3 py-2 flex gap-2 items-start min-w-0"
                          data-testid={`general-notification-item-${n.id}`}
                        >
                          <span className="mt-1.5 h-2 w-2 rounded-full shrink-0 bg-blue-500" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium truncate">{n.title}</span>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {timeAgo(n.created_at)}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {n.message}
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); markGeneralRead(n.id); }}
                          className="shrink-0 mr-2 mt-2 h-5 w-5 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground opacity-60 group-hover:opacity-100"
                          aria-label="Clear notification"
                          data-testid={`general-notification-clear-${n.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {(unreadSupportTickets || []).map((t) => (
                      <div
                        key={t.id}
                        className="group relative w-full flex gap-2 items-start border-b last:border-b-0 hover:bg-muted/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => handleNotificationClick(t.id)}
                          className="flex-1 text-left px-3 py-2 flex gap-2 items-start min-w-0"
                          data-testid={`notification-item-${t.id}`}
                        >
                          <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${priorityDotClass(t.priority)}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium truncate">
                                {t.ticket_number ? `New activity · ${t.ticket_number}` : 'New activity on ticket'}
                              </span>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {timeAgo(t.updated_at)}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {t.subject}
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleClearNotification(e, t.id)}
                          className="shrink-0 mr-2 mt-2 h-5 w-5 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground opacity-60 group-hover:opacity-100"
                          aria-label="Clear notification"
                          data-testid={`notification-clear-${t.id}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <DropdownMenuSeparator className="m-0" />
                <button
                  type="button"
                  onClick={() => navigate('/support')}
                  className="w-full text-center text-xs py-2 hover:bg-muted/50"
                  data-testid="notifications-view-all"
                >
                  View all support tickets
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            {/* Settings (Admin + Accounts) */}
            {!isSuspendedAccount && (isAdmin || hasPermission(user, 'billing.read') || hasPermission(user, 'accounting.read')) && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`h-8 w-8 ${topBarText}`}
                    onClick={() => navigate('/settings')}
                    data-testid="settings-nav-btn"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Settings</TooltipContent>
              </Tooltip>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`h-8 gap-2 px-2 ${topBarText} ${isLightMode ? 'hover:bg-[#3a3a40]' : 'hover:bg-zinc-800'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isLightMode ? 'bg-zinc-600' : 'bg-zinc-700'}`}>
                    <User className={`h-3.5 w-3.5 ${isLightMode ? 'text-zinc-300' : 'text-zinc-300'}`} />
                  </div>
                  <span className={`hidden md:block text-xs font-medium max-w-[100px] truncate ${isLightMode ? 'text-zinc-300' : 'text-zinc-300'}`}>
                    {user?.full_name}
                  </span>
                  <ChevronDown className={`h-3 w-3 hidden md:block ${isLightMode ? 'text-zinc-400' : 'text-zinc-400'}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.username || user?.email} • {getUserRoles(user).map(r => ROLE_LABELS[r] || r).join(', ')}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPreferencesOpen(true)}>
                  <Globe className="h-4 w-4 mr-2" />
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Layout (below top bar) */}
        <div className="flex min-h-screen" style={{ paddingTop: contentTopOffset }}>
          {/* Sidebar */}
          <aside
            style={{ top: contentTopOffset, height: `calc(100vh - ${contentTopOffset}px)` }}
            className={`fixed left-0 ${sidebarBg} transition-all duration-200 z-40 flex flex-col ${
              sidebarCollapsed ? 'w-14' : 'w-56'
            } ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}
          >
            {/* Navigation */}
            <ScrollArea className="flex-1 py-3">
              <nav className={`space-y-1 ${sidebarCollapsed ? 'px-2' : 'px-2'}`}>
                {effectiveNavItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </nav>
            </ScrollArea>

            {/* Early Adopter Badge */}
            {isEarlyAdopter && !sidebarCollapsed && (
              <div className="px-3 py-1.5">
                <div
                  data-testid="early-adopter-badge"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#ffd108]/10 border border-[#ffd108]/20"
                >
                  <Star className="h-3.5 w-3.5 text-[#ffd108] fill-[#ffd108]" />
                  <span className="text-[11px] font-semibold text-[#ffd108] tracking-wide">EARLY ADOPTER</span>
                </div>
              </div>
            )}
            {isEarlyAdopter && sidebarCollapsed && (
              <div className="flex justify-center py-1.5">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className="p-1.5 rounded bg-[#ffd108]/10 border border-[#ffd108]/20">
                      <Star className="h-3.5 w-3.5 text-[#ffd108] fill-[#ffd108]" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">Early Adopter</TooltipContent>
                </Tooltip>
              </div>
            )}

            {/* Version Number */}
            <div className={`px-3 py-2 ${sidebarCollapsed ? 'text-center' : 'flex items-center justify-between'}`}>
              <span className={`text-[10px] font-mono ${isLightMode ? 'text-zinc-400' : 'text-zinc-500'}`} data-testid="app-version">
                {getVersionDisplay(sidebarCollapsed)}
              </span>
              {!sidebarCollapsed && getEnvironment() !== 'PROD' && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  getEnvironment() === 'STAGING' ? 'bg-amber-500/20 text-amber-400' :
                  getEnvironment() === 'PREVIEW' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {getEnvironment()}
                </span>
              )}
            </div>

            {/* Collapse Toggle at Bottom */}
            <div className={`border-t ${sidebarBorder} p-2 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    data-testid="sidebar-collapse-btn"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${sidebarText} ${sidebarHover} transition-colors ${
                      sidebarCollapsed ? 'justify-center w-10 h-10' : 'w-full'
                    }`}
                  >
                    {sidebarCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <>
                        <PanelLeftClose className="h-4 w-4" />
                        <span className="hidden md:inline">Collapse</span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                )}
              </Tooltip>
            </div>
          </aside>

          {/* Overlay for mobile */}
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className={`flex-1 transition-all duration-200 ${
            sidebarCollapsed ? 'md:ml-14' : 'md:ml-56'
          }`}>
            {/* Impersonation Banner */}
            {isImpersonating && (
              <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Impersonating: <strong>{user?.full_name}</strong> ({user?.client_id})
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:text-white hover:bg-orange-600"
                >
                  <LogOut className="h-4 w-4 mr-1" /> End
                </Button>
              </div>
            )}
            <AnnouncementBanner />
            <div className="p-4 md:p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* User Preferences Dialog */}
      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Preferences
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Language Selection */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-base font-medium">{t('common.language')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('settings.languageDescription')}
                </p>
              </div>
              <Select value={currentLanguage} onValueChange={changeLanguage}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Theme Toggle */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="space-y-1">
                <Label className="text-base font-medium">Appearance</Label>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark mode
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Sun className={`h-4 w-4 ${theme === 'light' ? 'text-[#ffd108]' : 'text-muted-foreground'}`} />
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                />
                <Moon className={`h-4 w-4 ${theme === 'dark' ? 'text-[#ffd108]' : 'text-muted-foreground'}`} />
              </div>
            </div>

            {/* User Info */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Account</Label>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span>{user?.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Username</span>
                  <span className="font-mono">{user?.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role</span>
                  <span className="capitalize">{getUserRoles(user).map(r => ROLE_LABELS[r] || r).join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company</span>
                  <span>{user?.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client ID</span>
                  <span className="font-mono text-xs">{user?.client_id}</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreferencesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Early Adopter Feedback Dialog */}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

    </TooltipProvider>
  );
};

export default Layout;
